import anime from 'animejs/lib/anime.es.js'
import Lenis from '@studio-freight/lenis'

import { Raf } from '@/core/Raf'
import { Block } from '@/core/Block.js'
import { Layout } from '@/core/Layout.js'
import { SearchResult } from './templates/SearchResult.js'

export class NavSearch extends Block {
  init() {
    super.init()

    this.trigger = this.get('.c-nav-bar-search', this.page)
    this.closeButton = this.get('.c-nav-search-close')
    this.searchInput = this.get('#c-nav-search-input')
    this.content = this.get('.c-nav-search-content')
    this.contentUl = this.get('.c-nav-search-content-ul')
    this.bg = this.get('.c-nav-search-bg')
    this.top = this.get('.c-nav-search-top')

    this.onRaf = this.onRaf.bind(this)
    this.onSearchInput = this.onSearchInput.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onClose = this.onClose.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)

    this.hideModal = this.hideModal.bind(this)
    window.closeSearch = this.hideModal

    this.el.style.opacity = 0
  }

  addEvents() {
    super.addEvents()

    if (this.trigger) this.trigger.addEventListener('click', this.onClick)
    if (this.closeButton) this.closeButton.addEventListener('click', this.onClose)
    if (this.searchInput) this.searchInput.addEventListener('keyup', this.onKeyUp)
    if (this.bg) this.bg.addEventListener('click', this.onClose)

    document.addEventListener('keydown', this.onKeyDown)
  }

  destroy() {
    super.destroy()

    if (this.trigger) this.trigger.removeEventListener('click', this.onClick)
    if (this.closeButton) this.closeButton.removeEventListener('click', this.onClose)
    if (this.searchInput) this.searchInput.removeEventListener('keyup', this.onKeyUp)
    if (this.bg) this.bg.removeEventListener('click', this.onClose)

    document.removeEventListener('keydown', this.onKeyDown)
  }

  onKeyDown(e) {
    if (e.keyCode === 27) {
      this.hideModal()
    }
  }

  onKeyUp(e) {
    clearTimeout(this.typingTimeout)
    this.typingTimeout = setTimeout(() => this.onSearchInput(e), 300)
  }

  onClick(e) {
    e.preventDefault()
    this.showModal()
  }

  onClose(e) {
    e.preventDefault()
    this.hideModal()
  }

  async onSearchInput(e) {
    const searchQuery = this.searchInput.value.toLowerCase()
    const data = await this.fetchData(this.el.dataset.searchEndpoint, searchQuery)
    this.globals = this.globals ?? (await this.fetchData(this.el.dataset.translationsEndpoint))

    const results = data.results
    if (results.length === 0) {
      this.content.classList.add('has-results')
      this.contentUl.innerHTML = `<li class="c-nav-search-content-li no-results">${this.globals.labelNoResults}</li>`
    } else {
      this.content.classList.add('has-results')
      this.renderResults(results)
    }

    if (searchQuery === '') this.clearResults()
  }

  async renderResults(results) {
    this.contentUl.innerHTML = ''
    results.forEach((result) => {
      result.type = result.type ? this.globals[`label${result.type.toLowerCase()}`] : result.type
      result.subtype = result.subtype ? this.globals[`label${result.subtype.toLowerCase()}`] : result.subtype
      this.contentUl.innerHTML += `${SearchResult(result)}`
    })
  }

  clearResults() {
    this.content.classList.remove('has-results')
    this.contentUl.innerHTML = ''
  }

  showModal() {
    this.clearResults()
    this.searchInput.value = ''
    this.el.style.pointerEvents = 'all'
    this.el.style.display = 'block'

    Layout.scroll.stop()

    this.scroll = new Lenis({
      wrapper: this.content,
      content: this.contentUl,
    })

    Raf.add(this.onRaf, this.id)

    anime({
      targets: this.el,
      opacity: [0, 1],
      duration: 300,
      easing: 'linear',
      complete: () => this.searchInput.focus(),
    })

    anime({
      targets: [this.top, this.content],
      opacity: [0, 1],
      translateY: [-50, 0],
      duration: 1200,
      easing: 'easeOutExpo',
    })
  }

  async fetchData(endpoint, query = null) {
    let response = null
    if (query) response = await fetch(`${endpoint}?query=${query}`)
    else response = await fetch(endpoint)
    const data = await response.json()

    return data
  }

  onRaf({ frame }) {
    this.scroll.raf(frame)
  }

  hideModal(force = false) {
    this.el.style.pointerEvents = 'none'

    Layout.scroll.start()
    this.scroll?.destroy()

    Raf.remove(this.id)

    anime({
      targets: this.el,
      opacity: 0,
      duration: force ? 0 : 500,
      easing: 'linear',
      complete: () => {
        this.el.style.display = 'none'
      },
    })
  }
}
